import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Link, Mail, MessageCircle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const ShareButtons = ({ product, url }) => {
  const [showDialog, setShowDialog] = useState(false);

  // Get the backend URL for OG endpoint (this URL has proper meta tags for social sharing)
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const ogShareUrl = `${backendUrl}/api/og/products/${product.id}`;
  
  // Use the OG URL for social platforms (they will redirect to the actual page)
  // But use the direct URL for clipboard copy and display

  // Create rich share message
  const shareTitle = `${product.name} - The Gem Atelier`;
  const shareMessage = `✨ Discover this exquisite ${product.carat_weight}ct ${product.gem_type} at The Gem Atelier!\n\n${product.name}\n💎 ${product.carat_weight} carats\n📍 Origin: ${product.origin || 'Natural'}\n💰 ${product.price_on_request ? 'Price on Request' : `$${product.price?.toLocaleString() || ''}`}\n\nView this stunning gemstone: ${url}`;
  
  const shareMessageShort = `✨ ${product.name} - ${product.carat_weight}ct ${product.gem_type} ${product.price_on_request ? '' : `($${product.price?.toLocaleString() || ''})`} at The Gem Atelier`;

  // Use OG URL for platforms that crawl for previews
  const encodedOgUrl = encodeURIComponent(ogShareUrl);
  const encodedDirectUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedMessage = encodeURIComponent(shareMessage);
  const encodedMessageShort = encodeURIComponent(shareMessageShort);

  const shareLinks = {
    // Use OG URL for platforms that show link previews
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`✨ ${product.name} - ${product.carat_weight}ct ${product.gem_type}\n\n${ogShareUrl}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedOgUrl}&text=${encodedMessageShort}`,
    telegram: `https://t.me/share/url?url=${encodedOgUrl}&text=${encodedMessageShort}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedMessage}`,
    sms: `sms:?body=${encodedMessage}`,
  };

  const handleShare = (platform) => {
    if (platform === 'copy') {
      // Copy the OG URL for sharing (it redirects to the product page)
      navigator.clipboard.writeText(ogShareUrl);
      toast.success('Share link copied! This link will show product preview on social media.');
      return;
    }

    if (platform === 'native' && navigator.share) {
      navigator.share({
        title: shareTitle,
        text: shareMessageShort,
        url: ogShareUrl, // Use OG URL for native share too
      }).catch(() => {});
      return;
    }

    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        variant="outline"
        className="border-black hover:bg-gray-100"
        size="lg"
      >
        <Share2 className="w-5 h-5 mr-2" />
        Share
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Share this Gemstone</DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {/* Product Preview */}
            <div className="flex gap-3 p-3 bg-gray-50 rounded-lg mb-6">
              <img
                src={product.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=100&q=80'}
                alt={product.name}
                className="w-20 h-20 object-cover rounded"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">{product.name}</h3>
                <p className="text-xs text-gray-600">
                  {product.carat_weight}ct • {product.gem_type}
                </p>
                <p className="text-sm font-serif mt-1">
                  {product.price_on_request ? 'Price on Request' : `$${product.price.toLocaleString()}`}
                </p>
              </div>
            </div>

            {/* Share Message Preview */}
            <div className="mb-6 p-3 bg-gray-50 rounded border text-xs">
              <p className="text-gray-700 whitespace-pre-line">{shareMessageShort}</p>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3">
              {/* Native Share (Mobile) */}
              {navigator.share && (
                <Button
                  onClick={() => handleShare('native')}
                  className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Share2 className="w-5 h-5" />
                  <span>Share</span>
                </Button>
              )}

              {/* WhatsApp */}
              <Button
                onClick={() => handleShare('whatsapp')}
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white"
              >
                <MessageCircle className="w-5 h-5" />
                <span>WhatsApp</span>
              </Button>

              {/* Facebook */}
              <Button
                onClick={() => handleShare('facebook')}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Facebook className="w-5 h-5" />
                <span>Facebook</span>
              </Button>

              {/* Twitter */}
              <Button
                onClick={() => handleShare('twitter')}
                className="flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white"
              >
                <Twitter className="w-5 h-5" />
                <span>Twitter</span>
              </Button>

              {/* Telegram */}
              <Button
                onClick={() => handleShare('telegram')}
                className="flex items-center justify-center gap-2 bg-blue-400 hover:bg-blue-500 text-white"
              >
                <Send className="w-5 h-5" />
                <span>Telegram</span>
              </Button>

              {/* Email */}
              <Button
                onClick={() => handleShare('email')}
                className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-800 text-white"
              >
                <Mail className="w-5 h-5" />
                <span>Email</span>
              </Button>

              {/* Copy Link */}
              <Button
                onClick={() => handleShare('copy')}
                className="flex items-center justify-center gap-2 bg-black hover:bg-gray-900 text-white col-span-2"
              >
                <Link className="w-5 h-5" />
                <span>Copy Link</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareButtons;
